/**
 * useColumnResize composable
 * Provides column resizing functionality for data tables
 * @param {Array} columns - Array of column definitions with optional width property
 * @param {String} storageKey - Unique key for localStorage persistence
 * @returns {Object} - Column resize functionality
 */
import { ref, onMounted, onUnmounted } from 'vue';

export function useColumnResize(columns, storageKey = 'table_columnWidths') {
  // State for tracking resize operation
  const resizingColumn = ref(null);
  const startX = ref(0);
  const startWidth = ref(0);
  
  // Initialize column widths from column definitions
  const columnWidths = ref(columns.map(column => column.width || 'auto'));
  
  /**
   * Start resizing a column
   * @param {MouseEvent} event - The mousedown event
   * @param {Number} index - The column index being resized
   */
  const startResize = (event, index) => {
    event.preventDefault();
    resizingColumn.value = index;
    startX.value = event.pageX;
    
    // Get current width of the column
    const thElement = event.target.parentElement;
    startWidth.value = thElement.offsetWidth;
    
    // Add event listeners for mousemove and mouseup
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', stopResize);
    
    // Add a class to the body to show we're resizing (changes cursor)
    document.body.classList.add('resizing-columns');
  };
  
  /**
   * Handle mouse movement during resize
   * @param {MouseEvent} event - The mousemove event
   */
  const onMouseMove = (event) => {
    if (resizingColumn.value === null) return;
    
    const delta = event.pageX - startX.value;
    const newWidth = Math.max(80, startWidth.value + delta) + 'px';
    
    // Update the column width
    columnWidths.value[resizingColumn.value] = newWidth;
  };
  
  /**
   * Stop resizing and save the new widths
   */
  const stopResize = () => {
    resizingColumn.value = null;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', stopResize);
    
    // Remove the resizing class
    document.body.classList.remove('resizing-columns');
    
    // Store column widths in localStorage for persistence
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnWidths.value));
    } catch (e) {
      console.warn(`Failed to save column widths to localStorage (${storageKey})`, e);
    }
  };
  
  /**
   * Load saved column widths from localStorage on mount
   */
  const loadSavedWidths = () => {
    try {
      const savedWidths = localStorage.getItem(storageKey);
      if (savedWidths) {
        const parsedWidths = JSON.parse(savedWidths);
        // Only use saved widths if the number of columns matches
        if (parsedWidths.length === columns.length) {
          columnWidths.value = parsedWidths;
        }
      }
    } catch (e) {
      console.warn(`Failed to load column widths from localStorage (${storageKey})`, e);
    }
  };
  
  // Load saved widths on mount
  onMounted(() => {
    loadSavedWidths();
  });
  
  // Cleanup event listeners on unmount
  onUnmounted(() => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', stopResize);
    document.body.classList.remove('resizing-columns');
  });
  
  return {
    columnWidths,
    startResize
  };
}

